import { useCallback, useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dateIt, isOverdue } from "@/lib/format";
import { exportCsv } from "@/lib/csv";
import { FOLLOWUP_TYPES, FOLLOWUP_STATUSES } from "@/lib/constants";
import { PageHeader, EmptyState, Field } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Download, Search, Pencil, Trash2, AlertTriangle } from "lucide-react";

const EMPTY = {
  linked_type: "contact", linked_id: "", linked_name: "", date: new Date().toISOString().slice(0, 10),
  type: "Chiamata", outcome: "", next_action: "", next_action_date: "", status: "Aperto", notes: "",
};

const STATUS_CLS = {
  "Aperto": "bg-blue-50 text-blue-700 border-blue-200",
  "In ritardo": "bg-rose-50 text-rose-700 border-rose-200",
  "Chiuso": "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function Followups() {
  const [rows, setRows] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => { setLoading(true); api.get("/followups").then((r) => setRows(r.data)).finally(() => setLoading(false)); }, []);
  useEffect(() => {
    load();
    api.get("/contacts").then((r) => setContacts(r.data));
    api.get("/properties").then((r) => setProperties(r.data));
  }, [load]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (status !== "all" && r.status !== status) return false;
    if (search && !`${r.linked_name} ${r.outcome} ${r.next_action}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [rows, search, status]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (f) => { setEditing(f); setForm({ ...EMPTY, ...f }); setOpen(true); };

  const linkOptions = form.linked_type === "contact" ? contacts.map((c) => ({ id: c.id, name: c.name })) : properties.map((p) => ({ id: p.id, name: p.address }));

  const save = async () => {
    if (!form.linked_id) { toast.error("Seleziona un contatto o immobile collegato"); return; }
    setSaving(true);
    try {
      const opt = linkOptions.find((o) => o.id === form.linked_id);
      const payload = { ...form, linked_name: opt ? opt.name : form.linked_name };
      if (editing) await api.put(`/followups/${editing.id}`, payload);
      else await api.post("/followups", payload);
      toast.success(editing ? "Follow-up aggiornato" : "Follow-up creato");
      setOpen(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const del = async (f) => { try { await api.delete(`/followups/${f.id}`); toast.success("Follow-up eliminato"); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };

  const csv = () => exportCsv("followup_investart.csv", filtered, [
    { label: "ID", key: "id" }, { label: "Collegato a", key: "linked_name" }, { label: "Tipo collegamento", key: "linked_type" },
    { label: "Data", value: (r) => dateIt(r.date) }, { label: "Tipo", key: "type" }, { label: "Esito", key: "outcome" },
    { label: "Responsabile", key: "responsible_name" }, { label: "Prossima azione", key: "next_action" },
    { label: "Data prossima azione", value: (r) => dateIt(r.next_action_date) }, { label: "Stato", key: "status" },
  ]);

  return (
    <div data-testid="followups-page">
      <PageHeader title="Follow-up" subtitle={`${filtered.length} attività · Promemoria interni`}>
        <Button variant="outline" onClick={csv} data-testid="export-csv-followups"><Download className="h-4 w-4 mr-2" />CSV</Button>
        <Button onClick={openNew} data-testid="new-followup-btn"><Plus className="h-4 w-4 mr-2" />Nuovo Follow-up</Button>
      </PageHeader>

      <Card className="p-4 mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Cerca..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="followups-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full sm:w-52" data-testid="followup-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti gli stati</SelectItem>{FOLLOWUP_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Collegato a</TableHead><TableHead>Tipo</TableHead><TableHead>Esito</TableHead>
              <TableHead>Prossima Azione</TableHead><TableHead>Data</TableHead><TableHead>Responsabile</TableHead>
              <TableHead>Stato</TableHead><TableHead className="text-right">Azioni</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((f) => {
                const overdue = f.status !== "Chiuso" && isOverdue(f.next_action_date);
                return (
                  <TableRow key={f.id} data-testid={`followup-row-${f.id}`}>
                    <TableCell>
                      <div className="font-medium text-slate-800">{f.linked_name}</div>
                      <div className="text-xs text-muted-foreground capitalize">{f.linked_type === "contact" ? "Contatto" : "Immobile"}</div>
                    </TableCell>
                    <TableCell className="text-sm">{f.type}</TableCell>
                    <TableCell className="text-sm max-w-[160px] truncate">{f.outcome}</TableCell>
                    <TableCell className="text-sm max-w-[160px] truncate">{f.next_action}</TableCell>
                    <TableCell className="text-sm">
                      <span className={overdue ? "text-rose-600 font-semibold inline-flex items-center gap-1" : ""}>
                        {overdue && <AlertTriangle className="h-3.5 w-3.5" />}{dateIt(f.next_action_date)}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">{f.responsible_name}</TableCell>
                    <TableCell><span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS_CLS[f.status] || ""}`}>{f.status}</span></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(f)} data-testid={`edit-followup-${f.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-500" onClick={() => del(f)} data-testid={`delete-followup-${f.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        {!loading && filtered.length === 0 && <EmptyState>Nessun follow-up trovato.</EmptyState>}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifica Follow-up" : "Nuovo Follow-up"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 py-2">
            <Field label="Collegato a">
              <Select value={form.linked_type} onValueChange={(v) => setForm({ ...form, linked_type: v, linked_id: "" })}>
                <SelectTrigger data-testid="followup-linktype"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="contact">Contatto</SelectItem><SelectItem value="property">Immobile</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label={form.linked_type === "contact" ? "Contatto" : "Immobile"}>
              <Select value={form.linked_id} onValueChange={(v) => setForm({ ...form, linked_id: v })}>
                <SelectTrigger data-testid="followup-linkid"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{linkOptions.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Data"><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
            <Field label="Tipo attività">
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{FOLLOWUP_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Esito" className="sm:col-span-2"><Input value={form.outcome} onChange={(e) => setForm({ ...form, outcome: e.target.value })} /></Field>
            <Field label="Prossima azione"><Input value={form.next_action} onChange={(e) => setForm({ ...form, next_action: e.target.value })} /></Field>
            <Field label="Data prossima azione"><Input type="date" value={form.next_action_date} onChange={(e) => setForm({ ...form, next_action_date: e.target.value })} /></Field>
            <Field label="Stato">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="followup-status"><SelectValue /></SelectTrigger>
                <SelectContent>{FOLLOWUP_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Note"><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} disabled={saving} data-testid="save-followup-btn">{saving ? "Salvataggio..." : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
